import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import GameIcon from "components/admin/GameIcon";
import { itemTypeLabel } from "layouts/templates/labels";
import api from "api/client";

/** Danh sách loại vật phẩm đang có trong máy chủ, gọi một lần cho cả trang. */
let itemTypeCache = null;
function loadItemTypes() {
  if (!itemTypeCache) {
    itemTypeCache = api.get("/api/admin/templates/item-types").catch((e) => {
      itemTypeCache = null;
      throw e;
    });
  }
  return itemTypeCache;
}

/** PetRealmManager.ITEM_ADMIN: vật phẩm mở menu ADMIN, server chỉ cho tặng tài khoản admin. */
const ADMIN_ITEM_TYPE = -1;

const SOURCES = {
  item: {
    path: "/api/admin/templates/items",
    id: (x) => x.itemId,
    icon: (x) => x.iconPath,
    fallback: "inventory_2",
    text: (x) => `${x.name} (#${x.itemId})`,
    note: (x) => (x.isStackable ? null : "không cộng dồn"),
  },
  pet: {
    path: "/api/admin/templates/pets",
    id: (x) => x.petId,
    icon: (x) => x.icon,
    fallback: "pets",
    text: (x) => `${x.name} (#${x.petId})`,
    note: () => null,
  },
};

/**
 * Ô chọn vật phẩm / pet mẫu có tìm kiếm theo tên hoặc id, kèm icon như trong game. value là id (số) hoặc null.
 * Tìm theo tên không cần đúng dấu ("cuong hoa" ra "bùa cường hoá"). itemType khoá ô chọn vào một loại
 * (vd. 17 = ngọc khảm); không truyền thì hiện thêm ô "Loại" để xem hết vật phẩm theo từng loại.
 */
function TemplatePicker({
  kind,
  label,
  value,
  onChange,
  fullWidth,
  size,
  itemType,
  query,
  hideAdminItems,
}) {
  const source = SOURCES[kind];
  const [input, setInput] = useState("");
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  /** Lọc theo loại: itemType là loại cố định của ô chọn, filterType do người dùng chọn. */
  const [filterType, setFilterType] = useState("");
  const [itemTypes, setItemTypes] = useState([]);
  const showTypeFilter = kind === "item" && itemType == null;
  const type = itemType != null ? itemType : filterType || null;

  useEffect(() => {
    if (!showTypeFilter) return;
    loadItemTypes()
      .then((res) => setItemTypes(res))
      .catch(() => setItemTypes([]));
  }, [showTypeFilter]);

  useEffect(() => {
    if (!value) {
      setSelected(null);
    } else if (!selected || source.id(selected) !== Number(value)) {
      api
        .get(source.path, { q: String(value), size: 20 })
        .then((res) => setSelected(res.items.find((x) => source.id(x) === Number(value)) || null))
        .catch(() => setSelected(null));
    }
  }, [value, kind]);

  /** Object mới mỗi lần render nên so sánh bằng chuỗi, không thì useEffect chạy vô tận. */
  const queryKey = JSON.stringify(query || {});

  useEffect(() => {
    let active = true;
    setLoading(true);
    const timer = setTimeout(() => {
      api
        .get(source.path, { q: input, size: 30, type, ...JSON.parse(queryKey) })
        .then(
          (res) =>
            active &&
            setOptions(
              hideAdminItems ? res.items.filter((x) => x.type !== ADMIN_ITEM_TYPE) : res.items
            )
        )
        .catch(() => active && setOptions([]))
        .finally(() => active && setLoading(false));
    }, 300);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [input, source.path, type, queryKey, hideAdminItems]);

  const picker = (
    <Autocomplete
      fullWidth={fullWidth}
      size={size}
      value={selected}
      options={
        selected && !options.some((x) => source.id(x) === source.id(selected))
          ? [selected, ...options]
          : options
      }
      filterOptions={(x) => x}
      getOptionLabel={(x) => source.text(x)}
      isOptionEqualToValue={(a, b) => source.id(a) === source.id(b)}
      loading={loading}
      noOptionsText="Không tìm thấy"
      loadingText="Đang tìm..."
      onInputChange={(event, text, reason) => reason === "input" && setInput(text)}
      onChange={(event, option) => {
        setSelected(option);
        onChange(option ? source.id(option) : null, option);
      }}
      renderOption={(props, option) => (
        <MDBox component="li" {...props} key={source.id(option)} display="flex" gap={1}>
          <GameIcon path={source.icon(option)} size={28} fallback={source.fallback} />
          <MDBox lineHeight={1.1}>
            <MDTypography variant="button" display="block">
              {source.text(option)}
            </MDTypography>
            {source.note(option) && (
              <MDTypography variant="caption" color="text">
                {source.note(option)}
              </MDTypography>
            )}
          </MDBox>
        </MDBox>
      )}
      renderInput={(params) => (
        <MDInput
          {...params}
          label={label}
          InputProps={{
            ...params.InputProps,
            startAdornment: selected ? (
              <InputAdornment position="start" sx={{ ml: 0.5 }}>
                <GameIcon path={source.icon(selected)} size={24} fallback={source.fallback} />
              </InputAdornment>
            ) : null,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );

  if (!showTypeFilter) return picker;

  return (
    <MDBox display="flex" gap={1} alignItems="flex-start">
      <MDInput
        select
        size={size}
        label="Loại"
        value={filterType}
        onChange={(event) => setFilterType(event.target.value)}
        sx={{ flexShrink: 0, minWidth: 170 }}
        SelectProps={{ sx: size === "small" ? undefined : { height: 44 } }}
      >
        <MenuItem value="">Tất cả</MenuItem>
        {itemTypes.map((t) => (
          <MenuItem key={t.type} value={t.type}>
            {`${itemTypeLabel(t.type)} (${t.count})`}
          </MenuItem>
        ))}
      </MDInput>
      <MDBox flexGrow={1} minWidth={0}>
        {picker}
      </MDBox>
    </MDBox>
  );
}

TemplatePicker.defaultProps = {
  label: "",
  value: null,
  fullWidth: true,
  size: "medium",
  itemType: null,
  query: null,
  hideAdminItems: false,
};

TemplatePicker.propTypes = {
  kind: PropTypes.oneOf(["item", "pet"]).isRequired,
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  fullWidth: PropTypes.bool,
  size: PropTypes.oneOf(["small", "medium"]),
  itemType: PropTypes.number,
  /** Tham số lọc thêm gửi kèm khi tìm, vd. { petStr, petAgi, petInt }. */
  query: PropTypes.object,
  /** Ẩn vật phẩm admin (loại -1), dùng khi người nhận không phải admin. */
  hideAdminItems: PropTypes.bool,
};

export default TemplatePicker;
